import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  makeStyles,
} from '@material-ui/core';
import { getCottageName, sortCottagesByName } from '../utils';
import FilePreviewCard from './FilePreviewCard';

const useStyles = makeStyles({
  row: {
    display: 'flex',
    marginBottom: 4,
    marginLeft: -8,
    marginRight: -8,
    paddingLeft: 8,
    paddingRight: 8,
    borderRadius: 4,
  },
  rowSelected: {
    backgroundColor: '#f5f0eb',
  },
  rowLabel: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
  },
  title: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0,
  },
  count: {
    marginLeft: 8,
    flexShrink: 0,
    color: '#999',
  },
});

const ListingPickerDialog = ({
  open,
  cottages,
  counts,
  maxCount,
  initialListingIds,
  disabled,
  file,
  actions,
  onClose,
}) => {
  const classes = useStyles();
  const [listingIds, setListingIds] = React.useState([]);

  const cottagesRef = React.useRef(cottages);
  const initialListingIdsRef = React.useRef(initialListingIds);
  cottagesRef.current = cottages;
  initialListingIdsRef.current = initialListingIds;

  React.useEffect(() => {
    if (!open) return;
    const validIds = new Set((cottagesRef.current || []).map((c) => c.id));
    const seed = (initialListingIdsRef.current || []).filter((id) => validIds.has(id));
    setListingIds([...new Set(seed)]);
  }, [open]);

  const getListingCount = (cottageId) =>
    (counts && counts.listings && counts.listings[cottageId]) || 0;

  const sortedCottages = React.useMemo(() => sortCottagesByName(cottages), [cottages]);

  const selectableCottages = sortedCottages.filter(
    (c) => listingIds.includes(c.id) || getListingCount(c.id) < maxCount,
  );
  const allSelected =
    selectableCottages.length > 0 && selectableCottages.every((c) => listingIds.includes(c.id));

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const ids = selectableCottages.map((c) => c.id);
      setListingIds((prev) => [...new Set([...prev, ...ids])]);
    } else {
      const selectableIds = new Set(selectableCottages.map((c) => c.id));
      setListingIds((prev) => prev.filter((id) => !selectableIds.has(id)));
    }
  };

  const handleListingChange = (cottageId) => (e) => {
    if (e.target.checked) {
      setListingIds((prev) => [...prev, cottageId]);
    } else {
      setListingIds((prev) => prev.filter((id) => id !== cottageId));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <FormattedMessage
          id="hostFiles.associationTitle"
          defaultMessage="Which listings should the file be linked to?"
        />
      </DialogTitle>
      <DialogContent>
        {file && (
          <FilePreviewCard
            name={file.name}
            mimeType={file.mimeType}
            sizeBytes={file.sizeBytes}
            previewUrl={file.previewUrl}
          />
        )}

        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              checked={allSelected}
              indeterminate={listingIds.length > 0 && !allSelected}
              onChange={handleSelectAll}
              disabled={disabled || selectableCottages.length === 0}
            />
          }
          label={<FormattedMessage id="hostFiles.selectAll" defaultMessage="Select all" />}
          style={{ display: 'block', marginBottom: 8 }}
        />

        {sortedCottages.map((cottage) => {
          const listingCount = getListingCount(cottage.id);
          const full = listingCount >= maxCount;
          const checked = listingIds.includes(cottage.id);
          return (
            <FormControlLabel
              key={cottage.id}
              className={`${classes.row} ${checked ? classes.rowSelected : ''}`}
              classes={{ label: classes.rowLabel }}
              control={
                <Checkbox
                  color="primary"
                  checked={checked}
                  onChange={handleListingChange(cottage.id)}
                  disabled={disabled || (full && !checked)}
                />
              }
              label={
                <>
                  <span className={classes.title}>
                    #{cottage.id} - {getCottageName(cottage)}
                  </span>
                  <span className={classes.count}>
                    ({listingCount}/{maxCount})
                  </span>
                </>
              }
            />
          );
        })}
      </DialogContent>
      <DialogActions>{actions(listingIds)}</DialogActions>
    </Dialog>
  );
};

ListingPickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  cottages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title_sv: PropTypes.string,
    }),
  ).isRequired,
  counts: PropTypes.shape({
    listings: PropTypes.shape({}),
  }),
  maxCount: PropTypes.number.isRequired,
  initialListingIds: PropTypes.arrayOf(PropTypes.number),
  disabled: PropTypes.bool,
  file: PropTypes.shape({
    name: PropTypes.string.isRequired,
    mimeType: PropTypes.string,
    sizeBytes: PropTypes.number,
    previewUrl: PropTypes.string,
  }),
  actions: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

ListingPickerDialog.defaultProps = {
  initialListingIds: [],
  disabled: false,
  file: null,
};

export default ListingPickerDialog;
